import { useCallback, useMemo, useRef } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { usePaginatedAgentByConfigReference } from '@atlassian/conversation-assistant-agent/controllers/agents';
import {
	createHookWithAssistanceService,
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useGlobalConfig } from '@atlassian/conversation-assistant/common/utils/global-config';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import { fireAgentProvisioningErrorAnalytics } from '../utils/analytics';
import { CONTENT_REVIEWER_AGENT_KEY, provisionOotbAgent } from '../utils/api';
import { getConfiguredContentReviewerAgentId } from '../utils/content-reviewer-agent-config';

type GetAgentByConfigReference = ReturnType<
	typeof usePaginatedAgentByConfigReference
>['getAndStoreAgentByExternalConfigReference'];

const useAgentByIdResolver: (
	props: AssistanceServiceInputParam,
) => (
	agentId: string,
) => ReturnType<
	AssistanceServiceInjectedParams['assistanceServiceInjectedParams']['assistanceService']['getAgentDetails']
> = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams: { assistanceService } }: AssistanceServiceInjectedParams) => {
		const assistanceServiceRef = useRef(assistanceService);
		assistanceServiceRef.current = assistanceService;

		return useCallback(
			(agentId: string) => assistanceServiceRef.current.getAgentDetails(agentId),
			[],
		);
	},
);

/** Typed off the lookup, to avoid depending on the assistance service's API types. */
export type ContentReviewerAgent = NonNullable<Awaited<ReturnType<GetAgentByConfigReference>>>;

export type ResolveAgentOptions = {
	/** Resolve this configured agent directly, without entering the OOTB path. */
	agentId?: string;
	signal?: AbortSignal;
};

/**
 * Resolves the Content Reviewer agent — its identity (name, avatar) and the agent metadata a
 * conversation is created with. Resolution is left to the caller so it happens exactly when the
 * agent is wanted: a card resolves it on mount, the skill at send time.
 */
export function useContentReviewerAgentResolver({
	api,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
}): {
	resolveAgent: (options?: ResolveAgentOptions) => Promise<ContentReviewerAgent | undefined>;
} {
	// The Rovo provider owns the product/site identity the assistance service needs, so read it
	// from global config rather than making this plugin's consumers pass it in.
	const { productHeader, siteId, experienceId } = useGlobalConfig();
	const assistanceServiceParams = {
		product: productHeader,
		experienceId,
		siteId,
		touchpointSource: 'editor-ai',
	} as const;
	const { getAndStoreAgentByExternalConfigReference } = usePaginatedAgentByConfigReference({
		assistanceServiceParams,
	});
	const getAgentById = useAgentByIdResolver({ assistanceServiceParams });

	// Read through refs so the resolver stays stable however often the global config values —
	// and with them the lookup's identity — change.
	const getAgentRef = useRef(getAndStoreAgentByExternalConfigReference);
	getAgentRef.current = getAndStoreAgentByExternalConfigReference;
	const getAgentByIdRef = useRef(getAgentById);
	getAgentByIdRef.current = getAgentById;
	const siteIdRef = useRef(siteId);
	siteIdRef.current = siteId;
	const analyticsApiRef = useRef(api?.analytics?.actions);
	analyticsApiRef.current = api?.analytics?.actions;

	const resolveAgent = useCallback(async ({ signal, agentId }: ResolveAgentOptions = {}) => {
		if (agentId) {
			return getAgentByIdRef.current(agentId);
		}
		const configuredAgentId = getConfiguredContentReviewerAgentId();
		if (configuredAgentId) {
			return getAgentByIdRef.current(configuredAgentId);
		}

		// Provisioning is idempotent and must happen first: on a site that has never used
		// suggested edits the agent doesn't exist yet, so the lookup would come back empty. A
		// failure isn't fatal — the agent may exist from an earlier session — so report and
		// still attempt the lookup.
		try {
			await provisionOotbAgent({ cloudId: siteIdRef.current, signal });
		} catch (error) {
			fireAgentProvisioningErrorAnalytics(analyticsApiRef.current, error);
		}

		return getAgentRef.current({ externalConfigReference: CONTENT_REVIEWER_AGENT_KEY });
	}, []);

	// Memoised so the object is as stable as the resolver it carries, and safe to use directly
	// in a caller's effect deps.
	return useMemo(() => ({ resolveAgent }), [resolveAgent]);
}
